// src/services/SessionCleanupService.ts
/**
 * 🧹 Session Cleanup Service
 * Chiude automaticamente le sessioni zombie (più vecchie di 2 ore)
 */

import { supabase } from '../config/supabase';

export class SessionCleanupService {
  private static cleanupInterval: NodeJS.Timeout | null = null;
  private static readonly CLEANUP_INTERVAL_MS = 5 * 60 * 1000; // 5 minuti
  private static readonly MAX_SESSION_AGE_HOURS = 2;

  /**
   * Avvia il cleanup automatico
   */
  static startAutomaticCleanup(): void {
    if (this.cleanupInterval) {
      console.log('⚠️ Cleanup already running');
      return;
    }

    console.log('🧹 Starting automatic session cleanup (every 5 minutes)...');

    // Esegui subito la prima volta
    this.cleanupZombieSessions();

    // Poi ogni 5 minuti
    this.cleanupInterval = setInterval(() => {
      this.cleanupZombieSessions();
    }, this.CLEANUP_INTERVAL_MS);
  }

  /**
   * Ferma il cleanup automatico
   */
  static stopAutomaticCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
      console.log('🛑 Automatic session cleanup stopped');
    }
  }

  /**
   * Chiude le sessioni zombie (più vecchie di 2 ore)
   */
  static async cleanupZombieSessions(): Promise<{ cleaned: number; error?: string }> {
    try {
      const cutoffTime = new Date();
      cutoffTime.setHours(cutoffTime.getHours() - this.MAX_SESSION_AGE_HOURS);

      console.log(`🧹 Cleaning sessions older than ${cutoffTime.toISOString()}...`);

      const { data, error } = await supabase
        .from('user_sessions')
        .update({
          is_active: false,
          session_end: new Date().toISOString()
        })
        .eq('is_active', true)
        .lt('last_activity', cutoffTime.toISOString())
        .select('id');

      if (error) {
        console.error('❌ Error cleaning sessions:', error);
        return { cleaned: 0, error: error.message };
      }

      const cleanedCount = data?.length || 0;

      if (cleanedCount > 0) {
        console.log(`✅ Cleaned ${cleanedCount} zombie sessions`);
      }

      return { cleaned: cleanedCount };

    } catch (error: any) {
      console.error('❌ Exception during cleanup:', error);
      return { cleaned: 0, error: error.message };
    }
  }

  /**
   * Ottieni statistiche sessioni zombie
   */
  static async getZombieSessionsCount(): Promise<number> {
    try {
      const cutoffTime = new Date();
      cutoffTime.setHours(cutoffTime.getHours() - this.MAX_SESSION_AGE_HOURS);

      const { data, error } = await supabase
        .from('user_sessions')
        .select('id', { count: 'exact', head: true })
        .eq('is_active', true)
        .lt('last_activity', cutoffTime.toISOString());

      if (error) {
        console.error('Error counting zombie sessions:', error);
        return 0;
      }

      return data?.length || 0;

    } catch (error) {
      console.error('Error counting zombie sessions:', error);
      return 0;
    }
  }
}
